import { type Vec2, vec } from '../math';

/**
 * Geometric value types.
 *
 * These describe *shapes*, not scene entities. A `Circle` here is a centre and a
 * radius; the scene entity that owns styling, an id, and a construction definition
 * arrives in M3 and is built on top of these. Keeping the two apart means every
 * algorithm in this folder is usable from the solver, the renderer and the exporters
 * without dragging entity concerns along.
 */

/** An infinite line through `origin` along `direction`. `direction` is always unit length. */
export interface Line {
  readonly origin: Vec2;
  readonly direction: Vec2;
}

/** A half-line starting at `origin`. `direction` is always unit length. */
export interface Ray {
  readonly origin: Vec2;
  readonly direction: Vec2;
}

/** A finite segment from `a` to `b`. */
export interface Segment {
  readonly a: Vec2;
  readonly b: Vec2;
}

export interface Circle {
  readonly center: Vec2;
  readonly radius: number;
}

/**
 * A circular arc.
 *
 * `startAngle`/`endAngle` are in radians, measured from the positive x-axis in world
 * space (y-up). `counterclockwise` disambiguates which of the two arcs is meant; see
 * `Angle.isOnArc` for the exact sweep semantics, which match `ctx.arc`.
 */
export interface Arc {
  readonly center: Vec2;
  readonly radius: number;
  readonly startAngle: number;
  readonly endAngle: number;
  readonly counterclockwise: boolean;
}

/** An axis-aligned ellipse rotated by `rotation` radians about its centre. */
export interface Ellipse {
  readonly center: Vec2;
  readonly rx: number;
  readonly ry: number;
  readonly rotation: number;
}

/** An ordered ring of vertices. Not required to be closed — the closing edge is implied. */
export type Polygon = readonly Vec2[];

/** An axis-aligned bounding box. */
export interface Bounds {
  readonly minX: number;
  readonly minY: number;
  readonly maxX: number;
  readonly maxY: number;
}

/* ------------------------------------------------------------------ constructors */

/**
 * Line through `origin` along `direction`.
 *
 * Normalises eagerly. Every downstream algorithm assumes a unit direction, and
 * normalising once at construction is both cheaper and safer than each consumer
 * re-normalising (or forgetting to).
 */
export function line(origin: Vec2, direction: Vec2): Line {
  return { origin, direction: vec.normalize(direction) };
}

/** Line through two distinct points. */
export function lineThrough(a: Vec2, b: Vec2): Line {
  return { origin: a, direction: vec.normalize(vec.subtract(b, a)) };
}

export function ray(origin: Vec2, direction: Vec2): Ray {
  return { origin, direction: vec.normalize(direction) };
}

export function rayThrough(origin: Vec2, through: Vec2): Ray {
  return { origin, direction: vec.normalize(vec.subtract(through, origin)) };
}

export function segment(a: Vec2, b: Vec2): Segment {
  return { a, b };
}

export function circle(center: Vec2, radius: number): Circle {
  return { center, radius: Math.abs(radius) };
}

export function circleThrough(center: Vec2, through: Vec2): Circle {
  return { center, radius: vec.distance(center, through) };
}

export function arc(
  center: Vec2,
  radius: number,
  startAngle: number,
  endAngle: number,
  counterclockwise = false
): Arc {
  return { center, radius: Math.abs(radius), startAngle, endAngle, counterclockwise };
}

export function ellipse(center: Vec2, rx: number, ry: number, rotation = 0): Ellipse {
  return { center, rx: Math.abs(rx), ry: Math.abs(ry), rotation };
}

/** The infinite line containing a segment. Returns `null` for a degenerate segment. */
export function segmentToLine(s: Segment): Line | null {
  const d = vec.subtract(s.b, s.a);
  if (vec.isZero(d)) return null;
  return { origin: s.a, direction: vec.normalize(d) };
}

/** Direction from `a` to `b`, unit length. Zero for a degenerate segment. */
export function segmentDirection(s: Segment): Vec2 {
  return vec.normalize(vec.subtract(s.b, s.a));
}

export function segmentLength(s: Segment): number {
  return vec.distance(s.a, s.b);
}

/** A circle treated as a full-turn arc, for code that handles both uniformly. */
export function circleToArc(c: Circle): Arc {
  return {
    center: c.center,
    radius: c.radius,
    startAngle: 0,
    endAngle: Math.PI * 2,
    counterclockwise: false,
  };
}

/** A circle as an ellipse, for the ellipse-based intersection routines. */
export function circleToEllipse(c: Circle): Ellipse {
  return { center: c.center, rx: c.radius, ry: c.radius, rotation: 0 };
}
